/**
 * useOfflineQueue - session-backed mutation queue for spotty connectivity.
 *
 * When a network request fails (or returns 5xx), pages can enqueue the
 * mutation for replay. The queue persists in sessionStorage and replays in
 * order when the browser fires the `online` event.
 */
import { useCallback, useEffect, useState } from "react";

import { store } from "@/app/store";
import { baseApi } from "@/app/store/api/baseApi";
import { useAppSelector } from "@/app/store/hooks";

import { v1Url } from "@/shared/lib/authorizedFetch";
import { resolveRequestTenantSlug } from "@/shared/lib/tenantSlug";

const STORAGE_KEY = "tnn.offlineQueue";
const QUEUE_CHANGE_EVENT = "tnn:offline-queue-change";

export interface QueuedRequest {
  id: string;
  url: string;
  method: "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
}

/** True when an RTK Query mutation error should be queued for retry. */
export function isRetriableMutationError(err: unknown): boolean {
  if (!err || typeof err !== "object" || !("status" in err)) {
    return true;
  }
  const status = (err as { status: string | number }).status;
  if (
    status === "FETCH_ERROR" ||
    status === "PARSING_ERROR" ||
    status === "TIMEOUT_ERROR"
  ) {
    return true;
  }
  if (typeof status === "number" && status >= 500) {
    return true;
  }
  return false;
}

function readQueue(): QueuedRequest[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as QueuedRequest[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue(queue: QueuedRequest[]) {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  window.dispatchEvent(new CustomEvent(QUEUE_CHANGE_EVENT));
}

function buildAuthHeaders(): Record<string, string> {
  const state = store.getState();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const slug = resolveRequestTenantSlug({
    accessToken: state.auth.accessToken,
    user: state.auth.user,
    tenantSlug: state.auth.tenantSlug,
  });
  if (slug) {
    headers["X-Tenant-Slug"] = slug;
  }
  if (state.auth.accessToken) {
    headers.Authorization = `Bearer ${state.auth.accessToken}`;
  }
  return headers;
}

async function replayRequest(req: QueuedRequest): Promise<"success" | "retry" | "drop"> {
  try {
    const response = await fetch(v1Url(req.url), {
      method: req.method,
      headers: buildAuthHeaders(),
      body: req.body !== undefined ? JSON.stringify(req.body) : undefined,
    });
    if (response.status >= 400 && response.status < 500) {
      return "drop";
    }
    if (!response.ok) {
      return "retry";
    }
    return "success";
  } catch {
    return "retry";
  }
}

/** Hook: offline mutation queue with automatic replay on reconnect. */
export function useOfflineQueue() {
  const auth = useAppSelector((s) => s.auth);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  const [isSyncing, setIsSyncing] = useState(false);
  const [queueLength, setQueueLength] = useState(() => readQueue().length);

  const refreshQueueLength = useCallback(() => {
    setQueueLength(readQueue().length);
  }, []);

  const syncQueue = useCallback(async () => {
    if (isSyncing) return;
    const queue = readQueue();
    if (queue.length === 0) return;

    setIsSyncing(true);
    const remaining: QueuedRequest[] = [];
    let hadSuccess = false;

    for (const req of queue) {
      const result = await replayRequest(req);
      if (result === "success") {
        hadSuccess = true;
      } else if (result === "retry") {
        remaining.push(req);
      }
      // "drop" - validation error; omit from queue
    }

    writeQueue(remaining);
    setQueueLength(remaining.length);
    setIsSyncing(false);

    if (hadSuccess) {
      store.dispatch(
        baseApi.util.invalidateTags([
          { type: "CheckIn", id: "LIST" },
          { type: "CheckIn", id: "TODAY" },
          { type: "Inventory", id: "CATS" },
          { type: "Inventory", id: "TXN" },
          { type: "Inventory", id: "SUMMARY" },
        ]),
      );
    }
  }, [isSyncing]);

  const enqueue = useCallback(
    (req: Omit<QueuedRequest, "id">) => {
      const queue = readQueue();
      queue.push({
        ...req,
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      });
      writeQueue(queue);
      setQueueLength(queue.length);
      if (navigator.onLine) {
        void syncQueue();
      }
    },
    [syncQueue],
  );

  useEffect(() => {
    const onQueueChange = () => refreshQueueLength();
    const onOnline = () => {
      setIsOnline(true);
      void syncQueue();
    };
    const onOffline = () => setIsOnline(false);

    window.addEventListener(QUEUE_CHANGE_EVENT, onQueueChange);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    return () => {
      window.removeEventListener(QUEUE_CHANGE_EVENT, onQueueChange);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [refreshQueueLength, syncQueue]);

  // Re-sync when auth becomes available (e.g. after login with queued items).
  useEffect(() => {
    if (auth.accessToken && isOnline && queueLength > 0 && !isSyncing) {
      void syncQueue();
    }
  }, [auth.accessToken, isOnline, queueLength, isSyncing, syncQueue]);

  return {
    enqueue,
    syncQueue,
    isOnline,
    isSyncing,
    queueLength,
  };
}
